import { ArrowLeft, ArrowRight, Check, Minus, Plus, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { ButtonLabel } from "../../components/ButtonLabel";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { apiRequest, describeApiError } from "../../lib/api";
import { track } from "../../lib/analytics";
import { useReservationConfig } from "../../lib/queries";
import { draftStorage } from "../../lib/reservations";
import type { ApiEnvelope, ReservationRequest } from "../../types";

/**
 * Flow D — "Diseñemos tu próximo viaje": the four-step personalized-trip form (3a). Conditional
 * fields appear only when they apply, "Atrás" never loses valid answers, the in-progress draft
 * survives the session, and the disclosure that nothing costs anything is always visible (D3–D7).
 */

const STEPS = ["Tu destino", "Viajeros y estilo", "Presupuesto", "Contacto"] as const;
const STYLES: Array<{ id: "own" | "group" | "private_guide" | "unsure"; title: string; copy: string }> = [
  { id: "own", title: "Por mi cuenta", copy: "Un viaje diseñado para ti, sin acompañamiento permanente." },
  { id: "group", title: "En grupo", copy: "Quiero conocer opciones de salidas grupales." },
  { id: "private_guide", title: "Con guía privado", copy: "Acompañamiento personalizado durante parte o todo el viaje." },
  { id: "unsure", title: "No estoy seguro", copy: "Prefiero que un especialista me recomiende cómo viajar." }
];
const HOTELS: Array<{ id: "3" | "4" | "5" | "5lux"; stars: string; copy: string }> = [
  { id: "3", stars: "3 ★", copy: "Turista" }, { id: "4", stars: "4 ★", copy: "Recomendable" }, { id: "5", stars: "5 ★", copy: "Premium" }, { id: "5lux", stars: "5 ★ Lujo", copy: "Lujo" }
];

export interface Draft {
  step: number;
  destinations: string[];
  destinationInput: string;
  wantsRecommendations: boolean;
  origin: string;
  datesMode: "exact" | "flexible";
  departureDate: string;
  returnDate: string;
  flexiblePeriod: string;
  flexibleDays: "3" | "7" | "7+" | "";
  durationText: string;
  adults: number;
  children: number;
  infants: number;
  childAges: number[];
  style: "own" | "group" | "private_guide" | "unsure" | "";
  budgetMode: "yes" | "approx" | "guidance" | "";
  budgetAmount: string;
  budgetCurrency: "MXN" | "USD";
  budgetScope: "person" | "trip";
  flights: "include" | "exclude" | "unsure" | "";
  hotelPreference: "3" | "4" | "5" | "5lux" | "";
  notes: string;
  fullName: string;
  email: string;
  phone: string;
  contactPreference: "whatsapp" | "email" | "call" | "";
  infoConfirmed: boolean;
  privacyAccepted: boolean;
  marketingConsent: boolean;
  idempotencyKey: string;
}

const emptyDraft = (): Draft => ({
  step: 0, destinations: [], destinationInput: "", wantsRecommendations: false, origin: "", datesMode: "exact", departureDate: "", returnDate: "",
  flexiblePeriod: "", flexibleDays: "", durationText: "", adults: 2, children: 0, infants: 0, childAges: [], style: "", budgetMode: "", budgetAmount: "",
  budgetCurrency: "MXN", budgetScope: "person", flights: "", hotelPreference: "", notes: "", fullName: "", email: "", phone: "", contactPreference: "",
  infoConfirmed: false, privacyAccepted: false, marketingConsent: false, idempotencyKey: crypto.randomUUID()
});

/**
 * Entry points elsewhere (Explora search, a region, a trip without dates, Inicio) open the form
 * with what the customer already told us: `?destino=Egipto&destino=Jordania&nota=…&recomendar=1`.
 * It is merged into the saved draft without overwriting what they typed before.
 */
export function applyPrefill(draft: Draft, params: URLSearchParams): Draft {
  const destinations = params.getAll("destino").flatMap((value) => value.split(",")).map((value) => value.trim()).filter(Boolean).slice(0, 10);
  const note = params.get("nota")?.trim().slice(0, 2000) ?? "";
  const recommend = params.get("recomendar") === "1";
  if (!destinations.length && !note && !recommend) return draft;
  const known = new Set(draft.destinations.map((value) => value.toLowerCase()));
  return {
    ...draft,
    step: 0,
    destinations: [...draft.destinations, ...destinations.filter((value) => !known.has(value.toLowerCase()))],
    wantsRecommendations: destinations.length ? draft.wantsRecommendations : recommend || draft.wantsRecommendations,
    notes: note && !draft.notes.includes(note) ? [draft.notes.trim(), note].filter(Boolean).join("\n") : draft.notes
  };
}

export function PersonalizedTripPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const config = useReservationConfig();
  const storage = useMemo(() => draftStorage<Draft>(`aventuria:personalized-draft:${user?.id ?? "anon"}`), [user?.id]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState<Draft>(() => applyPrefill(storage.read() ?? emptyDraft(), searchParams));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string[] | null>(null);
  const [done, setDone] = useState<ReservationRequest | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const step = draft.step;
  usePageHeader("Diseñemos tu próximo viaje", `Paso ${step + 1} de ${STEPS.length} · ${STEPS[step]}`);

  useEffect(() => { storage.write(draft); }, [draft, storage]);
  // The prefill is applied once; a reload must not add it again.
  useEffect(() => {
    if (searchParams.has("destino") || searchParams.has("nota") || searchParams.has("recomendar")) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);
  useEffect(() => {
    if (!user) return;
    setDraft((current) => ({ ...current, fullName: current.fullName || user.fullName, email: current.email || user.email, phone: current.phone || user.phone || "" }));
  }, [user]);
  useEffect(() => { headingRef.current?.focus(); }, [step]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((current) => ({ ...current, [key]: value }));
  const maxTravelers = config.data?.maxTravelers ?? 12;

  const nights = useMemo(() => {
    if (draft.datesMode !== "exact" || !draft.departureDate || !draft.returnDate) return null;
    const days = Math.round((new Date(draft.returnDate).getTime() - new Date(draft.departureDate).getTime()) / 86_400_000);
    return days >= 0 ? { days: days + 1, nights: days } : null;
  }, [draft.datesMode, draft.departureDate, draft.returnDate]);

  const addDestination = () => {
    const value = draft.destinationInput.trim().replace(/,$/, "");
    if (!value) return;
    if (!draft.destinations.some((item) => item.toLowerCase() === value.toLowerCase())) set("destinations", [...draft.destinations, value]);
    set("destinationInput", "");
    set("wantsRecommendations", false);
  };

  const validate = (index: number): Record<string, string> => {
    const next: Record<string, string> = {};
    if (index === 0) {
      if (!draft.destinations.length && !draft.wantsRecommendations) next.destinations = "Escribe al menos un destino o pide recomendaciones.";
      if (draft.origin.trim().length < 2) next.origin = "Indica desde dónde viajarías.";
      if (draft.datesMode === "exact") {
        if (!draft.departureDate || !draft.returnDate) next.dates = "Indica las fechas de salida y regreso.";
        else if (draft.returnDate < draft.departureDate) next.dates = "El regreso no puede ser antes de la salida.";
      } else if (!draft.flexiblePeriod.trim() && !draft.durationText.trim()) {
        next.flexible = "Cuéntanos el mes o periodo aproximado, o cuánto quieres que dure el viaje.";
      }
    }
    if (index === 1) {
      const travelers = draft.adults + draft.children + draft.infants;
      if (travelers < 1) next.travelers = "Indica al menos un viajero.";
      if (travelers > maxTravelers) next.travelers = `Podemos atender hasta ${maxTravelers} viajeros por solicitud.`;
      if (draft.children + draft.infants > 0 && draft.childAges.length !== draft.children + draft.infants) next.ages = "Indica la edad de cada menor al momento del viaje.";
      if (!draft.style) next.style = "Elige cómo te gustaría viajar.";
    }
    if (index === 2) {
      if (!draft.budgetMode) next.budgetMode = "Cuéntanos si tienes un presupuesto contemplado.";
      if (draft.budgetMode && draft.budgetMode !== "guidance") {
        const amount = Number(draft.budgetAmount.replace(/[^\d.]/g, ""));
        if (!Number.isFinite(amount) || amount <= 0) next.budgetAmount = "Ingresa un monto aproximado mayor a cero.";
      }
      if (!draft.flights) next.flights = "Indica si tu presupuesto incluye vuelos internacionales.";
    }
    if (index === 3) {
      if (draft.fullName.trim().length < 2) next.fullName = "Ingresa tu nombre completo.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())) next.email = "Ingresa un correo electrónico válido.";
      if (!/^\+?[0-9\s-]{10,20}$/.test(draft.phone.trim())) next.phone = "Ingresa un número de WhatsApp de 10 dígitos (puedes incluir la lada +52).";
      if (!draft.contactPreference) next.contactPreference = "Elige cómo prefieres que te contactemos.";
      if (!draft.infoConfirmed) next.infoConfirmed = "Confirma que la información es correcta.";
      if (!draft.privacyAccepted) next.privacyAccepted = "Acepta el Aviso de Privacidad para continuar.";
    }
    return next;
  };

  const goNext = () => {
    const next = validate(step);
    setErrors(next);
    if (Object.keys(next).length) { document.getElementById(`field-${Object.keys(next)[0]}`)?.focus(); return; }
    track("personalized_trip_step_completed", { request_draft_id: draft.idempotencyKey, step_number: step + 1 });
    set("step", Math.min(STEPS.length - 1, step + 1));
  };
  const goBack = () => { setErrors({}); set("step", Math.max(0, step - 1)); };
  const lastStepValid = Object.keys(validate(3)).length === 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (step < STEPS.length - 1) { goNext(); return; }
    const next = validate(3);
    setErrors(next);
    if (Object.keys(next).length) return;
    setSubmitting(true); setApiError(null);
    try {
      const payload = await apiRequest<ApiEnvelope<{ request: ReservationRequest }>>("/api/v1/reservations/personalized", {
        method: "POST",
        headers: { "idempotency-key": draft.idempotencyKey },
        body: JSON.stringify({
          destinations: draft.destinations, wantsRecommendations: draft.wantsRecommendations || draft.destinations.length === 0, origin: draft.origin.trim(),
          datesMode: draft.datesMode,
          ...(draft.datesMode === "exact" ? { departureDate: draft.departureDate, returnDate: draft.returnDate } : {}),
          ...(draft.datesMode === "flexible" ? { flexiblePeriod: draft.flexiblePeriod.trim() || undefined, flexibleDays: draft.flexibleDays || undefined, durationText: draft.durationText.trim() || undefined } : {}),
          adults: draft.adults, children: draft.children, infants: draft.infants, childAges: draft.childAges, style: draft.style,
          budgetMode: draft.budgetMode,
          ...(draft.budgetMode !== "guidance" ? { budgetAmount: Number(draft.budgetAmount.replace(/[^\d.]/g, "")), budgetCurrency: draft.budgetCurrency, budgetScope: draft.budgetScope } : {}),
          flights: draft.flights, ...(draft.hotelPreference ? { hotelPreference: draft.hotelPreference } : {}), notes: draft.notes.trim() || undefined,
          contact: { fullName: draft.fullName.trim(), email: draft.email.trim(), phone: draft.phone.trim() }, contactPreference: draft.contactPreference,
          infoConfirmed: true, privacyAccepted: true, marketingConsent: draft.marketingConsent
        })
      });
      storage.clear();
      setDone(payload.data.request);
    } catch (cause) {
      setApiError(describeApiError(cause, "No pudimos enviar tu solicitud. Tus respuestas se conservan; intenta de nuevo."));
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="page-container custom-page">
        <section className="card custom-done">
          <span className="done-check"><Check size={26} strokeWidth={3} aria-hidden="true" /></span>
          <h2>¡Tu viaje ya está tomando forma!</h2>
          <p>Recibimos tu solicitud. Un especialista de Aventuria revisará tus preferencias y se pondrá en contacto contigo para conocer más detalles.</p>
          <dl className="kv-list">
            <div><dt>Número de solicitud</dt><dd translate="no">{done.folio}</dd></div>
            <div><dt>Destinos</dt><dd>{done.trip.name}</dd></div>
            <div><dt>Contacto preferido</dt><dd>{draft.contactPreference === "whatsapp" ? "WhatsApp" : draft.contactPreference === "email" ? "Correo electrónico" : "Llamada"}</dd></div>
            <div><dt>Respuesta estimada</dt><dd>24 a 48 horas hábiles</dd></div>
          </dl>
          <div className="inline-actions">
            <button type="button" className="primary-button solid" onClick={() => navigate(`/solicitudes/${done.id}`)}>Ver mi solicitud</button>
            <button type="button" className="secondary-button" onClick={() => { setDraft(emptyDraft()); setDone(null); }}>Solicitar otro viaje</button>
          </div>
          <p className="resv-fine">Enviar tu solicitud no implica ningún costo ni compromiso de compra.</p>
        </section>
      </div>
    );
  }

  const travelers = draft.adults + draft.children + draft.infants;

  return (
    <div className="page-container custom-page">
      <div className="custom-grid">
        <aside className="custom-aside">
          <span className="hero-eyebrow">Viaje a la medida</span>
          <h2>Diseñemos tu próximo viaje</h2>
          <p>Cuéntanos qué tienes en mente. No necesitas tener todo decidido, nosotros te ayudamos a darle forma.</p>
          <ol className="step-rail" aria-label="Pasos">
            {STEPS.map((label, index) => <li key={label} className={index === step ? "current" : index < step ? "done" : ""}><span>{index < step ? <Check size={12} aria-hidden="true" /> : index + 1}</span>{label}</li>)}
          </ol>
          <p className="custom-disclosure"><Sparkles size={14} aria-hidden="true" /> Enviar tu solicitud no implica ningún costo ni compromiso de compra.</p>
        </aside>

        <form className="card custom-form" onSubmit={submit} noValidate>
          <span className="page-eyebrow">Paso {step + 1} de {STEPS.length}</span>
          <h2 ref={headingRef} tabIndex={-1}>{STEPS[step]}</h2>

          {step === 0 && (
            <>
              <div className="field-block">
                <label htmlFor="field-destinations">Destino o destinos <span aria-hidden="true">*</span></label>
                <div className="token-field">
                  {draft.destinations.map((destination) => (
                    <span key={destination} className="token">{destination} <button type="button" onClick={() => set("destinations", draft.destinations.filter((item) => item !== destination))} aria-label={`Quitar ${destination}`}><X size={12} aria-hidden="true" /></button></span>
                  ))}
                  <input id="field-destinations" value={draft.destinationInput} onChange={(event) => set("destinationInput", event.target.value)} onBlur={addDestination}
                    onKeyDown={(event) => { if (event.key === "Enter" || event.key === ",") { event.preventDefault(); addDestination(); } }} placeholder="Ej. Japón, Kioto y Tokio" aria-invalid={Boolean(errors.destinations)} />
                </div>
                <button type="button" className={`chip${draft.wantsRecommendations && !draft.destinations.length ? " active" : ""}`} aria-pressed={draft.wantsRecommendations && !draft.destinations.length} onClick={() => { set("destinations", []); set("wantsRecommendations", true); }}>Aún no lo sé, quiero recomendaciones</button>
                {errors.destinations && <span className="field-error" role="alert">{errors.destinations}</span>}
              </div>
              <div className="field-block">
                <label htmlFor="field-origin">¿Desde dónde viajarías? <span aria-hidden="true">*</span></label>
                <input id="field-origin" className="standalone-input" value={draft.origin} onChange={(event) => set("origin", event.target.value)} placeholder="Ej. Ciudad de México (MEX)" aria-invalid={Boolean(errors.origin)} />
                {errors.origin && <span className="field-error" role="alert">{errors.origin}</span>}
              </div>
              <div className="field-block">
                <span className="field-label" id="dates-label">¿Cuándo te gustaría viajar? <span aria-hidden="true">*</span></span>
                <div className="chip-row" role="radiogroup" aria-labelledby="dates-label">
                  <button type="button" role="radio" aria-checked={draft.datesMode === "exact"} className={`chip${draft.datesMode === "exact" ? " active" : ""}`} onClick={() => set("datesMode", "exact")}>Ya tengo fechas</button>
                  <button type="button" role="radio" aria-checked={draft.datesMode === "flexible"} className={`chip${draft.datesMode === "flexible" ? " active" : ""}`} onClick={() => set("datesMode", "flexible")}>Mis fechas son flexibles</button>
                </div>
              </div>
              {draft.datesMode === "exact" ? (
                <div className="field-grid">
                  <div className="field-block"><label htmlFor="field-dates">Fecha de salida</label><input id="field-dates" className="standalone-input" type="date" value={draft.departureDate} onChange={(event) => set("departureDate", event.target.value)} aria-invalid={Boolean(errors.dates)} /></div>
                  <div className="field-block"><label htmlFor="field-return">Fecha de regreso</label><input id="field-return" className="standalone-input" type="date" min={draft.departureDate || undefined} value={draft.returnDate} onChange={(event) => set("returnDate", event.target.value)} /></div>
                  {nights && <span className="field-hint" translate="no">Duración calculada: {nights.days} días / {nights.nights} noches</span>}
                  {errors.dates && <span className="field-error" role="alert">{errors.dates}</span>}
                </div>
              ) : (
                <>
                  <div className="field-block">
                    <label htmlFor="field-flexible">Mes o periodo aproximado</label>
                    <input id="field-flexible" className="standalone-input" value={draft.flexiblePeriod} onChange={(event) => set("flexiblePeriod", event.target.value)} placeholder="Ej. marzo o primavera 2027" />
                  </div>
                  <div className="field-block">
                    <span className="field-label" id="flex-days">¿Cuántos días puedes mover tus fechas?</span>
                    <div className="chip-row" role="radiogroup" aria-labelledby="flex-days">
                      {([["3", "± 3 días"], ["7", "± 7 días"], ["7+", "Más de 7 días"]] as const).map(([id, label]) => <button key={id} type="button" role="radio" aria-checked={draft.flexibleDays === id} className={`chip${draft.flexibleDays === id ? " active" : ""}`} onClick={() => set("flexibleDays", id)}>{label}</button>)}
                    </div>
                  </div>
                  <div className="field-block">
                    <label htmlFor="field-duration">¿Cuánto te gustaría que durara el viaje?</label>
                    <input id="field-duration" className="standalone-input" value={draft.durationText} onChange={(event) => set("durationText", event.target.value)} placeholder="Ej. 10 días" />
                    {errors.flexible && <span className="field-error" role="alert">{errors.flexible}</span>}
                  </div>
                </>
              )}
            </>
          )}

          {step === 1 && (
            <>
              <div className="field-block">
                <span className="field-label" id="field-travelers">¿Quiénes viajarían? <span aria-hidden="true">*</span></span>
                <div className="pax-grid" role="group" aria-labelledby="field-travelers">
                  {([["adults", "Adultos"], ["children", "Niñas y niños"], ["infants", "Bebés"]] as const).map(([key, label]) => (
                    <div key={key} className="pax-row">
                      <span>{label}</span>
                      <div className="stepper small">
                        <button type="button" onClick={() => { const value = Math.max(0, draft[key] - 1); set(key, value); if (key !== "adults") set("childAges", draft.childAges.slice(0, draft.children + draft.infants - 1)); }} aria-label={`Menos ${label.toLowerCase()}`}><Minus size={14} aria-hidden="true" /></button>
                        <strong translate="no">{draft[key]}</strong>
                        <button type="button" onClick={() => { if (travelers >= maxTravelers) return; set(key, draft[key] + 1); if (key !== "adults") set("childAges", [...draft.childAges, key === "infants" ? 1 : 8]); }} aria-label={`Más ${label.toLowerCase()}`}><Plus size={14} aria-hidden="true" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.travelers && <span className="field-error" role="alert">{errors.travelers}</span>}
              </div>
              {draft.children + draft.infants > 0 && (
                <div className="field-block">
                  <span className="field-label">Edades al momento del viaje <span aria-hidden="true">*</span></span>
                  <div className="age-grid">
                    {draft.childAges.map((age, index) => (
                      <label key={index} className="age-field"><span>Menor {index + 1}</span>
                        <input className="standalone-input small" inputMode="numeric" value={String(age)} onChange={(event) => set("childAges", draft.childAges.map((value, i) => (i === index ? Math.max(0, Math.min(17, Number(event.target.value.replace(/\D/g, "")) || 0)) : value)))} aria-label={`Edad menor ${index + 1}`} />
                      </label>
                    ))}
                  </div>
                  {errors.ages && <span className="field-error" role="alert">{errors.ages}</span>}
                </div>
              )}
              <div className="field-block">
                <span className="field-label" id="field-style">¿Cómo te gustaría viajar? <span aria-hidden="true">*</span></span>
                <div className="style-cards" role="radiogroup" aria-labelledby="field-style">
                  {STYLES.map((style) => (
                    <button key={style.id} type="button" role="radio" aria-checked={draft.style === style.id} className={`style-card${draft.style === style.id ? " selected" : ""}`} onClick={() => set("style", style.id)}>
                      <span className="plan-radio" aria-hidden="true" /><strong>{style.title}</strong><span>{style.copy}</span>
                    </button>
                  ))}
                </div>
                {errors.style && <span className="field-error" role="alert">{errors.style}</span>}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="field-block">
                <span className="field-label" id="field-budgetMode">¿Tienes un presupuesto contemplado?</span>
                <div className="chip-row" role="radiogroup" aria-labelledby="field-budgetMode">
                  {([["yes", "Sí"], ["approx", "Tengo una idea aproximada"], ["guidance", "No, quiero orientación"]] as const).map(([id, label]) => <button key={id} type="button" role="radio" aria-checked={draft.budgetMode === id} className={`chip${draft.budgetMode === id ? " active" : ""}`} onClick={() => set("budgetMode", id)}>{label}</button>)}
                </div>
                {errors.budgetMode && <span className="field-error" role="alert">{errors.budgetMode}</span>}
              </div>
              {draft.budgetMode && draft.budgetMode !== "guidance" && (
                <div className="field-block">
                  <label htmlFor="field-budgetAmount">¿Cuál es tu presupuesto aproximado?</label>
                  <div className="chip-row">
                    {(["MXN", "USD"] as const).map((currency) => <button key={currency} type="button" className={`chip small${draft.budgetCurrency === currency ? " active" : ""}`} aria-pressed={draft.budgetCurrency === currency} onClick={() => set("budgetCurrency", currency)}>{currency}</button>)}
                  </div>
                  <input id="field-budgetAmount" className="standalone-input" inputMode="decimal" value={draft.budgetAmount} onChange={(event) => set("budgetAmount", event.target.value.replace(/[^\d.,]/g, ""))} placeholder="Ej. 60,000" aria-invalid={Boolean(errors.budgetAmount)} />
                  <div className="chip-row">
                    {([["person", "Por persona"], ["trip", "Para todo el viaje"]] as const).map(([id, label]) => <button key={id} type="button" className={`chip small${draft.budgetScope === id ? " active" : ""}`} aria-pressed={draft.budgetScope === id} onClick={() => set("budgetScope", id)}>{label}</button>)}
                  </div>
                  <span className="field-hint">No tiene que ser exacto. Nos ayudará a recomendarte opciones adecuadas.</span>
                  {errors.budgetAmount && <span className="field-error" role="alert">{errors.budgetAmount}</span>}
                </div>
              )}
              <div className="field-block">
                <span className="field-label" id="field-flights">¿Tu presupuesto incluye vuelos internacionales?</span>
                <div className="chip-row" role="radiogroup" aria-labelledby="field-flights">
                  {([["include", "Incluir vuelos internacionales"], ["exclude", "No incluirlos"], ["unsure", "No estoy seguro"]] as const).map(([id, label]) => <button key={id} type="button" role="radio" aria-checked={draft.flights === id} className={`chip${draft.flights === id ? " active" : ""}`} onClick={() => set("flights", id)}>{label}</button>)}
                </div>
                {errors.flights && <span className="field-error" role="alert">{errors.flights}</span>}
              </div>
              <div className="field-block">
                <span className="field-label">¿Qué tipo de hospedaje prefieres? <span className="label-optional">· opcional</span></span>
                <div className="hotel-cards" role="radiogroup" aria-label="Tipo de hospedaje">
                  {HOTELS.map((hotel) => (
                    <button key={hotel.id} type="button" role="radio" aria-checked={draft.hotelPreference === hotel.id} className={`hotel-card${draft.hotelPreference === hotel.id ? " selected" : ""}`} onClick={() => set("hotelPreference", draft.hotelPreference === hotel.id ? "" : hotel.id)}>
                      <strong>{hotel.stars}</strong><span>{hotel.copy}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="field-block">
                <label htmlFor="field-notes">¿Hay algo más que debamos saber? <span className="label-optional">· opcional</span></label>
                <textarea id="field-notes" className="standalone-input textarea" rows={3} value={draft.notes} onChange={(event) => set("notes", event.target.value)} maxLength={2000} placeholder="Cuéntanos sobre celebraciones especiales, lugares que quieres visitar, necesidades de accesibilidad o cualquier otra preferencia." />
              </div>
              <div className="field-grid">
                <div className="field-block"><label htmlFor="field-fullName">Nombre completo <span aria-hidden="true">*</span></label><input id="field-fullName" className="standalone-input" value={draft.fullName} onChange={(event) => set("fullName", event.target.value)} autoComplete="name" aria-invalid={Boolean(errors.fullName)} />{errors.fullName && <span className="field-error" role="alert">{errors.fullName}</span>}</div>
                <div className="field-block"><label htmlFor="field-phone">Número de WhatsApp <span aria-hidden="true">*</span></label><input id="field-phone" className="standalone-input" value={draft.phone} onChange={(event) => set("phone", event.target.value)} inputMode="tel" autoComplete="tel" placeholder="+52 55 1234 5678" aria-invalid={Boolean(errors.phone)} />{errors.phone && <span className="field-error" role="alert">{errors.phone}</span>}</div>
              </div>
              <div className="field-block"><label htmlFor="field-email">Correo electrónico <span aria-hidden="true">*</span></label><input id="field-email" className="standalone-input" type="email" value={draft.email} onChange={(event) => set("email", event.target.value)} autoComplete="email" aria-invalid={Boolean(errors.email)} />{errors.email && <span className="field-error" role="alert">{errors.email}</span>}</div>
              <div className="field-block">
                <span className="field-label" id="field-contactPreference">¿Cómo prefieres que te contactemos? <span aria-hidden="true">*</span></span>
                <div className="chip-row" role="radiogroup" aria-labelledby="field-contactPreference">
                  {([["whatsapp", "WhatsApp"], ["email", "Correo electrónico"], ["call", "Llamada"]] as const).map(([id, label]) => <button key={id} type="button" role="radio" aria-checked={draft.contactPreference === id} className={`chip${draft.contactPreference === id ? " active" : ""}`} onClick={() => set("contactPreference", id)}>{label}</button>)}
                </div>
                {errors.contactPreference && <span className="field-error" role="alert">{errors.contactPreference}</span>}
              </div>
              <label className={`consent-row${draft.infoConfirmed ? " checked" : ""}`}>
                <input id="field-infoConfirmed" type="checkbox" checked={draft.infoConfirmed} onChange={(event) => set("infoConfirmed", event.target.checked)} />
                <span className="consent-box" aria-hidden="true">{draft.infoConfirmed && <Check size={13} strokeWidth={3} />}</span>
                <span>Confirmo que la información proporcionada es correcta. <span aria-hidden="true">*</span></span>
              </label>
              <label className={`consent-row${draft.privacyAccepted ? " checked" : ""}`}>
                <input id="field-privacyAccepted" type="checkbox" checked={draft.privacyAccepted} onChange={(event) => set("privacyAccepted", event.target.checked)} />
                <span className="consent-box" aria-hidden="true">{draft.privacyAccepted && <Check size={13} strokeWidth={3} />}</span>
                <span>Acepto el <a href="/aviso-de-privacidad" target="_blank" rel="noreferrer">Aviso de Privacidad de Aventuria</a>. <span aria-hidden="true">*</span></span>
              </label>
              <label className={`consent-row${draft.marketingConsent ? " checked" : ""}`}>
                <input type="checkbox" checked={draft.marketingConsent} onChange={(event) => set("marketingConsent", event.target.checked)} />
                <span className="consent-box" aria-hidden="true">{draft.marketingConsent && <Check size={13} strokeWidth={3} />}</span>
                <span>Quiero recibir recomendaciones, promociones e inspiración para futuros viajes. <span className="label-optional">· opcional</span></span>
              </label>
              {(errors.infoConfirmed || errors.privacyAccepted) && <span className="field-error" role="alert">{errors.infoConfirmed ?? errors.privacyAccepted}</span>}
            </>
          )}

          {apiError && <div className="form-error" role="alert">{apiError.join(" ")}</div>}
          <div className="custom-actions">
            {step > 0 ? <button type="button" className="secondary-button" onClick={goBack}><ArrowLeft size={15} aria-hidden="true" /> Atrás</button> : <span />}
            <span className="resv-helper">Enviar tu solicitud no implica ningún costo ni compromiso de compra.</span>
            {step < STEPS.length - 1
              ? <button type="button" className="primary-button solid" onClick={goNext}>Continuar <ArrowRight size={15} aria-hidden="true" /></button>
              : <button type="submit" className="primary-button solid" disabled={submitting || !lastStepValid}><ButtonLabel busy={submitting} busyLabel="Enviando" label="Solicitar propuesta" /></button>}
          </div>
        </form>
      </div>
    </div>
  );
}
